import { useLazyQuery } from "@apollo/client";
import { useEffect, useRef } from "react";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { ORGANISATION_ONBOARDING_STATUS } from "@/graphql/organisations/shared";
import useBackoff from "@/hooks/use-backoff";

export default function useOrganisationOnboardingStatus() {
  const { organisation, refetchOrganisations } = useOrganisationContext();
  const [backoff, { next, reset }] = useBackoff({
    initial: 1000,
    max: 8000,
  });

  const [fetchStatus, { data, loading, error }] = useLazyQuery(ORGANISATION_ONBOARDING_STATUS, {
    variables: {
      organisation: organisation?.id,
    },
    fetchPolicy: "network-only",
  });

  const onboardingStatus = data?.connectedAccount?.onboardingStatus;

  // Track backoff delay in a ref so the polling effect can read it
  // without re-running when it changes.
  const backoffRef = useRef(backoff);
  useEffect(() => {
    backoffRef.current = backoff;
  }, [backoff]);

  // Track whether we've already fired the terminal refetch for the
  // current status transition, so it only fires once.
  const didTerminalRefetch = useRef(false);

  // Guard against the refetchOrganisations → organisation update →
  // fetchStatus → refetchOrganisations loop: only allow the org-change
  // effect to fire fetchStatus while actively polling.
  const isPollingRef = useRef(true);

  // Track the current organisation id so we can detect org switches
  // and re-arm polling even after a terminal state.
  const orgIdRef = useRef(organisation?.id);

  useEffect(() => {
    const currentOrgId = organisation?.id;

    if (currentOrgId !== orgIdRef.current) {
      // Organisation changed — reset polling state for the new org.
      orgIdRef.current = currentOrgId;
      isPollingRef.current = true;
      didTerminalRefetch.current = false;
      reset();
    }

    if (organisation && isPollingRef.current) {
      fetchStatus();
    }
  }, [organisation, fetchStatus, reset]);

  // Polling effect: only poll while in_progress.
  useEffect(() => {
    if (!data || loading || !data.connectedAccount) return;

    if (onboardingStatus === "in_progress") {
      // Re-arm polling flag and clear terminal refetch flag;
      // reset backoff to 1s if resuming after a terminal state.
      isPollingRef.current = true;
      if (didTerminalRefetch.current) {
        didTerminalRefetch.current = false;
        reset();
      }

      const timeout = setTimeout(() => {
        fetchStatus();
        next();
      }, backoffRef.current);

      return () => {
        clearTimeout(timeout);
      };
    } else {
      // Terminal state — stop polling, refetch orgs exactly once.
      isPollingRef.current = false;

      if (!didTerminalRefetch.current) {
        didTerminalRefetch.current = true;
        refetchOrganisations();
      }
    }
  }, [data, loading, onboardingStatus, fetchStatus, next, reset, refetchOrganisations]);

  return {
    onboardingStatus,
    loading,
    error,
  };
}
